/**
 * Generic types for the SavedView / Presets feature.
 *
 * Each domain (inventory, trades, availability, ...) instantiates this
 * with its own payload shape `P` via createSavedViewsController / Hooks.
 */

/**
 * Scope of a saved view. The backend serves `inventory` and `trades`
 * (`/api/v1/saved-views/<scope>/`, LR-671); `availability` is reserved for a
 * future domain — using it at runtime returns 404 until its viewset exists.
 */
export type Scope = 'inventory' | 'trades' | 'availability';

// Single source of truth for the scope literals. Adapter code should use
// these constants instead of hardcoding the strings so that TypeScript
// catches typos and future scope renames propagate automatically.
export const INVENTORY_SCOPE: Scope = 'inventory';
export const TRADES_SCOPE: Scope = 'trades';

export interface SavedView {
  id: number;
  name: string;
  description: string | null;
  scope: Scope;
  author: number | null;
  author_name: string;
  author_email: string | null;
  is_public: boolean;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

export interface SavedViewDetail<P = unknown> extends SavedView {
  payload: P;
}

export interface SavedViewListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SavedView[];
  default_view_id: number | null;
}

export interface CreateSavedViewRequest<P = unknown> {
  name: string;
  description?: string;
  is_public: boolean;
  payload: P;
}

export interface UpdateSavedViewRequest<P = unknown> {
  name?: string;
  description?: string;
  is_public?: boolean;
  payload?: P;
}

/**
 * Adapter for a single scope. Encapsulates everything domain-specific:
 * - `capture()` reads the current table state into a payload.
 * - `apply(payload)` writes the payload back into the domain's stores.
 *   Must tolerate empty/partial payloads (system views ship `{}`).
 * - `subscribeToChanges(listener)` fires when anything that could affect
 *   `capture()` output changes. Used by the selector to detect dirty state.
 *   Returns an unsubscribe function.
 */
export interface SavedViewsAdapter<P> {
  scope: Scope;
  capture: () => P;
  apply: (payload: P | Record<string, never>) => void;
  subscribeToChanges: (listener: () => void) => () => void;
  /** Optional structural equality override; defaults to `lodash.isequal`. */
  isEqualPayload?: (a: P, b: P) => boolean;
}
